/**
 * 资源路径修复工具
 * 用于统一处理网站资源路径，确保在生产环境中正确使用CDN前缀
 */

/**
 * 检查路径是否为视频文件
 * @param {string} path - 文件路径
 * @returns {boolean} 是否为视频文件
 */
function isVideoFile(path) {
  if (!path || typeof path !== 'string') return false;
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  const lowerPath = path.toLowerCase();
  return videoExtensions.some(ext => lowerPath.includes(ext));
}

/**
 * 获取 CDN 前缀（用于视频资源，无论 useCdn 值如何）
 * @returns {string|null} CDN 前缀
 */
function getCdnPrefixForVideo() {
  if (typeof window === 'undefined') return null;
  
  if (window.__INITIAL_SITE_CONFIG__ && window.__INITIAL_SITE_CONFIG__.cdnPrefix) {
    return window.__INITIAL_SITE_CONFIG__.cdnPrefix;
  }
  
  return null;
}

/**
 * 检查当前环境是否是开发环境
 * 兼容客户端和服务端
 */
function isDevelopment() {
  // 浏览器环境
  if (typeof window !== 'undefined' && typeof __ENV__ !== 'undefined') {
    return __ENV__ === 'development';
  }
  // Node环境
  else if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development';
  }
  return false;
}

/**
 * 检查当前环境是否是生产环境
 * 兼容客户端和服务端
 */
function isProduction() {
  // 浏览器环境
  if (typeof window !== 'undefined' && typeof __ENV__ !== 'undefined') {
    return __ENV__ === 'production';
  } 
  // Node环境
  else if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'release';
  }
  return false;
}

/**
 * 检测是否应该使用CDN路径
 * @returns {boolean} 是否应该使用CDN路径
 */
export function shouldUseCdn() {
  if (typeof window === 'undefined') return false;
  
  // 检查是否是生产环境
  if (isProduction()) {
    // 检查是否有站点配置
    if (window.__INITIAL_SITE_CONFIG__) {
      const { useCdn, cdnPrefix } = window.__INITIAL_SITE_CONFIG__;
      return Boolean(useCdn && cdnPrefix);
    }
  }
  
  return false;
}

/**
 * 获取CDN前缀
 * @returns {string|null} CDN前缀，如果不应使用CDN则返回null
 */
export function getCdnPrefix() {
  if (typeof window === 'undefined') return null;
  
  if (shouldUseCdn() && window.__INITIAL_SITE_CONFIG__) {
    return window.__INITIAL_SITE_CONFIG__.cdnPrefix;
  }
  
  return null;
}

/**
 * 修复资源URL，确保在生产环境中使用CDN前缀
 * 
 * @param {string} url - 要修复的URL
 * @returns {string} 修复后的URL
 */
export function fixAssetUrl(url) {
  // 如果URL是空或不是字符串，直接返回
  if (!url || typeof url !== 'string') return url;
  
  // 如果是完整的URL（包括协议），直接返回
  if (url.match(/^(https?:)?\/\//)) return url;
  
  try {
    // 开发环境需要特殊处理路径
    if (isDevelopment()) {
      // 开发环境中处理字体文件路径
      if (url.startsWith('/website/fonts/')) {
        return url.replace('/website/fonts/', '/fonts/');
      }
      // 开发环境中处理图片路径
      if (url.startsWith('/website/images/')) {
        return url.replace('/website/images/', '/images/');
      }
      return url;
    }
    
    // 视频文件始终使用 cdnPrefix（无论 useCdn 的值）
    if (isVideoFile(url)) {
      const videoCdnPrefix = getCdnPrefixForVideo();
      if (videoCdnPrefix) {
        if (url.startsWith('/website/')) {
          return `${videoCdnPrefix}${url}`;
        } else if (url.startsWith('/images/')) {
          return `${videoCdnPrefix}/website${url}`;
        } else if (url.startsWith('/')) {
          return `${videoCdnPrefix}${url}`;
        } else {
          return `${videoCdnPrefix}/${url}`;
        }
      }
      return url;
    }
    
    // 非视频资源：如果不应该使用CDN，直接返回
    if (!shouldUseCdn()) return url;
    
    const cdnPrefix = getCdnPrefix();
    if (!cdnPrefix) return url;
    
    // 处理以/website/开头的路径
    if (url.startsWith('/website/')) {
      return `${cdnPrefix}${url}`;
    }
    
    // 处理以/images/开头的路径（需要转换为/website/images/）
    if (url.startsWith('/images/')) {
      return `${cdnPrefix}/website${url}`;
    }
    
    // 处理以/fonts/开头的路径（需要转换为/website/fonts/）
    if (url.startsWith('/fonts/')) {
      return `${cdnPrefix}/website${url}`;
    }
    
    // 其他以/开头的路径，可能也需要考虑添加CDN前缀
    if (url.startsWith('/')) {
      return `${cdnPrefix}${url}`;
    }
  } catch (e) {
    console.error('资源路径处理错误:', e);
  }
  
  // 其他情况直接返回
  return url;
}

/**
 * Vue插件，用于自动修复Vue组件内的资源路径
 */
export const AssetPathFixerPlugin = {
  install(app) {
    // 全局注入fixAssetUrl方法
    app.config.globalProperties.$fixAssetUrl = fixAssetUrl;
    
    // 添加全局指令v-asset-url
    app.directive('asset-url', {
      beforeMount(el, binding) {
        const attr = binding.arg || 'src';
        if (el.hasAttribute(attr)) {
          const originalUrl = el.getAttribute(attr);
          el.setAttribute(attr, fixAssetUrl(originalUrl));
        }
      },
      updated(el, binding) {
        const attr = binding.arg || 'src';
        if (el.hasAttribute(attr)) {
          const originalUrl = el.getAttribute(attr);
          el.setAttribute(attr, fixAssetUrl(originalUrl));
        }
      }
    });
    
    // 在应用挂载后扫描所有图片和资源路径并修复
    if (typeof window !== 'undefined') {
      // 在DOM变化时进行静态资源路径扫描
      app.mixin({
        mounted() {
          // 在组件加载后处理组件内的所有图片
          if (!isProduction()) return;
          
          // 延迟执行以确保DOM完全渲染
          setTimeout(() => {
            if (this.$el) {
              fixAllResourcePaths(this.$el);
            }
          }, 100);
        },
        updated() {
          // 在组件更新后处理组件内的所有图片
          if (!isProduction()) return;
          
          // 延迟执行以确保DOM完全渲染
          setTimeout(() => {
            if (this.$el) {
              fixAllResourcePaths(this.$el);
            }
          }, 100);
        }
      });
      
      // 页面加载完成后全局处理一次
      window.addEventListener('load', () => {
        if (isProduction()) {
          fixAllResourcePaths(document.body);
        }
      });
    }
  }
};

/**
 * 修复DOM元素中的所有资源路径
 * @param {HTMLElement} rootElement - 根元素
 */
export function fixAllResourcePaths(rootElement) {

  if (!rootElement) return;
  
  try {
    // 处理所有图片的src属性
    const allImages = rootElement.querySelectorAll('img');
    allImages.forEach(img => {
      if (img.src && typeof img.src === 'string') {
        const originalSrc = img.getAttribute('src');
        if (originalSrc && !originalSrc.startsWith('http') && !originalSrc.startsWith('data:')) {
          const fixedSrc = fixAssetUrl(originalSrc);
          if (fixedSrc !== originalSrc) {
            img.src = fixedSrc;
          }
        }
      }
    });
    
    // 处理所有css背景图片
    const elementsWithStyle = rootElement.querySelectorAll('[style*="background"], [style*="background-image"]');
    elementsWithStyle.forEach(el => {
      const style = el.getAttribute('style');
      if (style && (style.includes('background') || style.includes('background-image'))) {
        const fixedStyle = fixStyleUrls(style);
        if (fixedStyle !== style) {
          el.setAttribute('style', fixedStyle);
        }
      }
    });
    
    // 处理链接中的href
    const links = rootElement.querySelectorAll('link[rel="stylesheet"], link[rel="preload"], link[rel="icon"], link[rel="shortcut icon"]');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href && !href.startsWith('http') && !href.startsWith('data:')) {
        const fixedHref = fixAssetUrl(href);
        if (fixedHref !== href) {
          link.setAttribute('href', fixedHref);
        }
      }
    });
    
    // 处理video, audio等媒体元素
    const mediaElements = rootElement.querySelectorAll('video, audio');
    mediaElements.forEach(media => {
      const src = media.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('data:')) {
        const fixedSrc = fixAssetUrl(src);
        if (fixedSrc !== src) {
          media.setAttribute('src', fixedSrc);
        }
      }
      
      // 处理source子元素
      const sources = media.querySelectorAll('source');
      sources.forEach(source => {
        const sourceSrc = source.getAttribute('src');
        if (sourceSrc && !sourceSrc.startsWith('http') && !sourceSrc.startsWith('data:')) {
          const fixedSourceSrc = fixAssetUrl(sourceSrc);
          if (fixedSourceSrc !== sourceSrc) {
            source.setAttribute('src', fixedSourceSrc);
          }
        }
      });
    });
    
    // 处理内联样式表
    const styleElements = rootElement.querySelectorAll('style');
    styleElements.forEach(styleEl => {
      const cssText = styleEl.textContent;
      if (cssText) {
        const fixedCssText = fixStyleUrls(cssText);
        if (fixedCssText !== cssText) {
          styleEl.textContent = fixedCssText;
        }
      }
    });
    
    // 处理特殊属性，如data-src (lazy loading)
    const elementsWithDataSrc = rootElement.querySelectorAll('[data-src]');
    elementsWithDataSrc.forEach(el => {
      const dataSrc = el.getAttribute('data-src');
      if (dataSrc && !dataSrc.startsWith('http') && !dataSrc.startsWith('data:')) {
        const fixedDataSrc = fixAssetUrl(dataSrc);
        if (fixedDataSrc !== dataSrc) {
          el.setAttribute('data-src', fixedDataSrc);
        }
      }
    });
  } catch (e) {
    // 静默处理错误
  }
}

/**
 * 处理CSS样式中的URL
 * @param {string} styleText - 样式文本
 * @returns {string} 修复后的样式文本
 */
function fixStyleUrls(styleText) {
  if (!styleText || typeof styleText !== 'string') return styleText;
  
  // 匹配样式中的url()表达式
  const urlRegex = /url\(['"]?([^'")]+)['"]?\)/g;
  return styleText.replace(urlRegex, (match, url) => {
    // 只处理本地资源路径
    if (url && !url.startsWith('http') && !url.startsWith('data:')) {
      const fixedUrl = fixAssetUrl(url);
      return `url('${fixedUrl}')`;
    }
    return match;
  });
}

/**
 * 为输出的HTML添加CDN前缀
 * 
 * @param {string} html - 原始HTML内容
 * @returns {string} 修复后的HTML内容
 */
export function fixHtmlAssetUrls(html) {
  if (!html) return html;
  
  try {
    // 检查是否是开发环境
    if (isDevelopment()) {
      // 在开发环境中修正路径，替换/website/为正确的路径
      return html
        .replace(/(href=["'])\/website\/fonts\//g, '$1/fonts/')
        .replace(/(src=["'])\/website\/fonts\//g, '$1/fonts/')
        .replace(/(href=["'])\/website\/images\//g, '$1/images/')
        .replace(/(src=["'])\/website\/images\//g, '$1/images/')
        .replace(/background-image:\s*url\(['"]?\/website\/images\//g, "background-image:url('/images/")
        .replace(/background:\s*url\(['"]?\/website\/images\//g, "background:url('/images/");
    }
  
    // 生产环境CDN处理
    // 在服务器端执行时缺少window对象，需要额外检查
    // 这里直接从传入的参数中获取cdnPrefix
    const cdnPrefix = typeof window !== 'undefined' && window.__INITIAL_SITE_CONFIG__?.cdnPrefix;
    
    if (cdnPrefix) {
      
      // 替换CSS和JS资源链接
      html = html
        // 替换href属性中的/website/路径
        .replace(/(href=["'])\/website\//g, `$1${cdnPrefix}/website/`)
        // 替换src属性中的/website/路径
        .replace(/(src=["'])\/website\//g, `$1${cdnPrefix}/website/`) 
        // 替换href属性中的/images/路径
        .replace(/(href=["'])\/images\//g, `$1${cdnPrefix}/website/images/`)
        // 替换src属性中的/images/路径
        .replace(/(src=["'])\/images\//g, `$1${cdnPrefix}/website/images/`)
        // 替换background-image:url('/images/...)路径
        .replace(/background-image:\s*url\(['"]?\/images\//g, `background-image:url(${cdnPrefix}/website/images/`)
        // 替换background:url('/images/...)路径
        .replace(/background:\s*url\(['"]?\/images\//g, `background:url(${cdnPrefix}/website/images/`)
        // 替换字体路径
        .replace(/(href=["'])\/fonts\//g, `$1${cdnPrefix}/website/fonts/`)
        .replace(/(src=["'])\/fonts\//g, `$1${cdnPrefix}/website/fonts/`);
        
      // 额外处理引号内的路径
      html = html
        .replace(/url\(['"]?\/website\/images\//g, `url(${cdnPrefix}/website/images/`)
        .replace(/url\(['"]?\/website\/fonts\//g, `url(${cdnPrefix}/website/fonts/`);
    }
    
    return html;
  } catch (e) {
    console.error('HTML资源路径处理错误:', e);
    return html; // 出错时返回原始HTML
  }
}

// 创建一个可以包装现有工具函数的辅助函数
export function wrapWithCdnSupport(originalFn) {
  return function(...args) {
    const result = originalFn(...args);
    return fixAssetUrl(result);
  };
} 